import { AHWidgetTitle, Box } from "arkh";
import React, { useCallback } from "react";

import { AddTodoButton } from "./AddTodoButtonC";
import { BaseWidgetProps } from "../BaseProps";

import { useEvent } from "../useEvent";
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  useSubscribeToInvalidationState,
} from "react-relay";
import { WidgetTodoOverviewQuery } from "./__generated__/WidgetTodoOverviewQuery.graphql";
import { WidgetTodoOverview_widget$key } from "./__generated__/WidgetTodoOverview_widget.graphql";

import { WidgetTodoOverviewList } from "./WidgetTodoOverviewList";
import { useSetScrollFocusMutation } from "../Focus/useSetFocusScroll";
import { useSetWidgetFocus } from "../useSetFocus";
import { WidgetTodoOverviewPreview_widget$key } from "./__generated__/WidgetTodoOverviewPreview_widget.graphql";

export const WidgetTodoOverview = (
  props: {
    widget: WidgetTodoOverview_widget$key;
  } & BaseWidgetProps
) => {
  const widget = useFragment(
    graphql`
      fragment WidgetTodoOverview_widget on WidgetTodoOverview {
        id
        title
        category {
          id
          title
        }
        teams {
          id
        }
      }
    `,
    props.widget
  );
  const [fetchKey, setFetchKey] = React.useState(0);
  useSubscribeToInvalidationState([widget.id], () => {
    setFetchKey((fk) => fk + 1);
  });

  const setFocus = useSetWidgetFocus(widget.id, props.storecastId);
  const onSelectTodo = useEvent((todoId: string) => {
    if (props.mode === "FOCUS" && props.highlightedItemId !== todoId) {
      setFocus(todoId);
    } else {
      if (widget.category) {
        props.onEvent?.({
          kind: "Todo",
          todoId,
          widgetId: widget.id,
          categoryId: widget.category?.id,
        });
      }
    }
  });
  const onEvent = props.onEvent;
  const interactive = props.interactive;
  const onRequestAddTodo = useCallback(
    (categoryId: string, categoryTitle: string, teamIds: string[]) =>
      onEvent?.({
        kind: "TodoCreate",
        categoryId,
        teamIds,
        categoryTitle,
      }),
    [onEvent]
  );

  const onPressAdd = useCallback(() => {
    if (!widget.category || interactive === false) {
      return;
    }
    onRequestAddTodo(
      widget.category.id,
      widget.category.title,
      widget?.teams?.map((t) => t.id) ?? []
    );
  }, [interactive, onRequestAddTodo, widget.category, widget.teams]);

  const query = useLazyLoadQuery<WidgetTodoOverviewQuery>(
    graphql`
      query WidgetTodoOverviewQuery(
        $widgetId: ID!
        $first: Int
        $after: String
      ) {
        widgetTodoOverview(id: $widgetId) {
          id
          ...WidgetTodoOverviewListPaginated
        }
      }
    `,
    {
      widgetId: widget.id,
    },
    {
      fetchKey: fetchKey + (props.fetchKey ?? ""),
      fetchPolicy: "store-and-network",
    }
  );

  const setScroll = useSetScrollFocusMutation(props.storecastId, widget.id);

  if (!query.widgetTodoOverview) {
    return null;
  }

  return (
    <Box style={{ flex: 1 }} color="surface">
      <AHWidgetTitle>{widget.title}</AHWidgetTitle>
      <WidgetTodoOverviewList
        isFocused={props.isFocused}
        mode={props.mode}
        interactive={props.interactive}
        highlightedItemId={props.highlightedItemId}
        onPressItem={onSelectTodo}
        setScroll={setScroll}
        widget={query.widgetTodoOverview}
      />
      {props.idiom === "STATIC" ? null : <AddTodoButton onPress={onPressAdd} />}
    </Box>
  );
};

function noOp() {}
export function WidgetTodoOverviewPreview(
  props: {
    widget: WidgetTodoOverviewPreview_widget$key;
  } & BaseWidgetProps
) {
  const widget = useFragment(
    graphql`
      fragment WidgetTodoOverviewPreview_widget on WidgetTodoOverview {
        id
        title
        category {
          id
          title
        }
        teams {
          id
        }
        ...WidgetTodoOverviewListPaginated
      }
    `,
    props.widget
  );

  return (
    <Box style={{ flex: 1 }} color="surface">
      <AHWidgetTitle>{widget.title}</AHWidgetTitle>
      <WidgetTodoOverviewList
        isFocused={false}
        mode="IDLE"
        interactive={false}
        onPressItem={noOp}
        setScroll={noOp}
        widget={widget}
      />
    </Box>
  );
}

export { WidgetTodoOverviewList };
